import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, ArrowRight } from "lucide-react";
import { WHATSAPP_LINK, CREDITARIA_URL } from "@/lib/api";

const FAQS = [
  {
    q: "¿De verdad es 100% gratis la asesoría? ¿Cuál es el truco?",
    a: "No hay truco. Así funciona el brokerage hipotecario. Cuando te ayudamos a obtener un crédito con un banco o financiera, esa institución nos paga una comisión por haberles llevado un cliente calificado. Tú no pagas absolutamente nada — ni en la tasa, ni en comisiones, ni en ningún concepto adicional. En muchos casos las condiciones que obtienes a través de nosotros son incluso MEJORES que si fueras directamente al banco, porque tenemos convenios especiales de volumen que nos permiten negociar condiciones preferenciales.",
  },
  {
    q: "¿Cuánto tiempo toma el proceso completo?",
    a: "La precalificación toma solo 3 minutos. La pre-aprobación con un banco toma de 3 a 5 días hábiles. El proceso completo desde la solicitud hasta la firma ante notario toma entre 30 y 60 días naturales para adquisición, y un poco más para construcción. Nosotros aceleramos los tiempos porque conocemos exactamente qué documentos necesita cada institución y cómo presentarlos desde el primer intento, evitando rechazos y solicitudes de información adicional.",
  },
  {
    q: "¿Qué requisitos necesito para solicitar un crédito hipotecario?",
    a: "Los requisitos básicos son: ser mayor de edad, tener ingresos comprobables (empleado, negocio propio, informal o sin comprobar — tenemos opciones para todos), buen historial crediticio y capacidad de pago. El enganche mínimo suele ser 10-20% del valor de la propiedad, aunque hay programas desde 5%. En tu precalificación gratis analizamos tu situación específica y te decimos exactamente qué opciones tienes disponibles.",
  },
  {
    q: "¿Y si mi historial crediticio no es perfecto?",
    a: "Tener un historial menos que perfecto no significa que estés descartado. Cada institución tiene criterios diferentes y, al comparar +15 opciones, frecuentemente encontramos alternativas viables para perfiles que fueron rechazados en otros lados. Además te asesoramos sobre cómo mejorar tu score crediticio y preparar tu perfil para obtener mejores condiciones. Lo importante es dar el primer paso.",
  },
  {
    q: "¿Con qué bancos e instituciones financieras trabajan?",
    a: "Trabajamos con las principales instituciones financieras bancarias y no bancarias de México, además de esquemas Infonavit, Fovissste, Cofinavit y Alia2 Plus. En total comparamos +15 opciones. No estamos casados con ningún banco, así que siempre recomendamos la opción que genuinamente te conviene más a ti.",
  },
  {
    q: "¿Puedo usar mi crédito Infonavit o Fovissste con ustedes?",
    a: "¡Por supuesto! Somos expertos en esquemas combinados como Cofinavit (Infonavit + banco) y Alia2 Plus (Fovissste + banco). Estos esquemas te permiten aprovechar tu subcuenta de vivienda y combinarla con un crédito bancario para acceder a una propiedad de mayor valor. Te asesoramos sobre cuál esquema conviene más a tu caso y gestionamos toda la coordinación entre instituciones.",
  },
  {
    q: "¿Puedo precalificarme si aún no tengo una propiedad elegida?",
    a: "¡Absolutamente! De hecho, es lo ideal. Así sabrás con exactitud cuánto te pueden prestar, cuál sería tu mensualidad y qué rango de precios puedes considerar. Con la precalificación en mano buscas tu propiedad con seguridad y ventaja negociadora — puedes presentarla al vendedor como prueba de capacidad de compra.",
  },
  {
    q: "¿Cómo sé que me conviene una sustitución de hipoteca?",
    a: "En tu asesoría gratuita hacemos un análisis detallado de tu hipoteca actual vs. las opciones de sustitución. Te mostramos con números exactos: cuánto ahorras en mensualidad, cuánto ahorras en intereses totales, cuánto cuesta el proceso y en cuántos meses recuperas esa inversión. Si los números no te convienen, te lo decimos con honestidad. En promedio, nuestros clientes que sustituyen ahorran entre $2,000 y $5,000 pesos al mes.",
  },
  {
    q: "¿Brillo Finance me va a presionar para tomar una decisión?",
    a: "Jamás. Nuestra filosofía es darte toda la información, responder tus preguntas y dejarte decidir a tu ritmo. No somos vendedores, somos asesores. El 98% de nuestros clientes nos recomienda precisamente porque nunca se sintieron presionados. Tú decides si, cuándo y con quién proceder — nosotros solo nos aseguramos de que tengas la mejor información para hacerlo.",
  },
  {
    q: "¿Brillo Finance me garantiza la aprobación del crédito?",
    a: "No. Ni Brillo Finance ni sus representantes pueden garantizar la aprobación de ningún crédito. Esto depende 100% de los criterios y políticas de cada institución financiera. Si alguien te promete lo contrario, repórtalo al +52 33 4406 5965 o a escuchamos@brillofinance.com. Lo que sí garantizamos: transparencia total, la mejor opción disponible para tu perfil y acompañamiento hasta la firma.",
  },
];

export default function FAQSection() {
  return (
    <section id="faq" className="py-16 bg-white" data-testid="faq-section">
      <div className="bf-container">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="bf-chip mb-3">
              <HelpCircle className="h-3.5 w-3.5" /> Preguntas frecuentes
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#333333] leading-tight">
              Todo lo que necesitas saber antes de dar el primer paso.
            </h2>
            <p className="mt-3 text-[#666666] leading-relaxed">
              Recopilamos las dudas más comunes. Si no encuentras tu respuesta, contáctanos por WhatsApp — te respondemos en menos de 24 horas.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={CREDITARIA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-cta-button"
                className="bf-btn-primary rounded-full px-5 py-3 font-semibold inline-flex items-center gap-2"
              >
                Precalificarme ahora <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-whatsapp-button"
                className="bf-btn-outline rounded-full px-5 py-3 font-semibold inline-flex items-center gap-2"
              >
                Preguntar por WhatsApp
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`item-${i}`}
                  className="border-b border-[#e5e7eb]"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-medium text-sm md:text-base text-[#333333] hover:text-[#f05c3e] hover:no-underline py-3.5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#666666] leading-relaxed pb-4 text-sm">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
